import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import Avatar from 'components/Avatar'
import CloseIcon from '@mui/icons-material/Close'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import SearchIcon from '@mui/icons-material/Search'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { searchAssignableUsers } from 'services/taskService'
import { getCurrentUser } from 'features/auth/ducks/selectors'

const roleWord = (label) =>
  label ? label.trim().replace(/s$/i, '').toLowerCase() : null

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const UserPicker = ({
  value = [],
  onChange,
  buildingPassportId,
  excludeIds = [],
  placeholder = 'Search users...',
  label,
  showEmailInvite = false,
  inviteEmails = [],
  onInviteEmailsChange,
  initialUsers = [],
  knownUsers = [],
  maxSelected = Infinity
}) => {
  const currentUser = useSelector(getCurrentUser)
  const [search, setSearch] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [emailInput, setEmailInput] = useState('')
  const [nameInput, setNameInput] = useState('')
  const [emailError, setEmailError] = useState('')
  const wrapperRef = useRef(null)
  const debounceRef = useRef(null)
  const emailInputRef = useRef(null)

  // Selected users cache (to show chips with names) — seeded from initialUsers
  const [selectedUsers, setSelectedUsers] = useState(initialUsers)

  const mergeUniqueUsers = useCallback(users => {
    const byId = new Map()
    users.forEach(user => {
      if (!user?.id) return
      byId.set(String(user.id), user)
    })
    return Array.from(byId.values())
  }, [])

  // Sync externally-added IDs (e.g. from team member buttons) into the cache
  useEffect(() => {
    const missingIds = value.filter(id => !selectedUsers.some(u => u.id === id))
    if (missingIds.length === 0) return
    const toAdd = knownUsers.filter(u => missingIds.includes(u.id))
    if (toAdd.length > 0) {
      setSelectedUsers(prev => mergeUniqueUsers([...prev, ...toAdd]))
    }
  }, [value, knownUsers, selectedUsers, mergeUniqueUsers])

  useEffect(() => {
    const handleClickOutside = e => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const doSearch = useCallback(
    async query => {
      const normalizedQuery = (query || '').trim()
      if (
        !buildingPassportId &&
        normalizedQuery.length > 0 &&
        normalizedQuery.length < 2
      ) {
        setResults([])
        return
      }
      setLoading(true)
      try {
        const res = await searchAssignableUsers({
          search: normalizedQuery,
          buildingPassportId
        })
        setResults(res.data?.data || [])
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    },
    [buildingPassportId]
  )

  const handleSearchChange = e => {
    const val = e.target.value
    setSearch(val)
    setOpen(true)

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => doSearch(val), 300)
  }

  const handleToggleOpen = () => {
    setOpen(prev => {
      const next = !prev
      if (next && results.length === 0 && !loading) {
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = setTimeout(() => doSearch(search), 0)
      }
      return next
    })
  }

  const handleSelect = user => {
    if (value.includes(user.id)) return
    const newValue = maxSelected === 1 ? [user.id] : [...value, user.id]
    setSelectedUsers(prev =>
      maxSelected === 1 ? [user] : mergeUniqueUsers([...prev, user])
    )
    onChange(newValue)
    if (maxSelected === 1) {
      setSearch('')
      setResults([])
      setOpen(false)
    }
  }

  const handleRemove = userId => {
    onChange(value.filter(id => id !== userId))
    setSelectedUsers(prev => prev.filter(u => u.id !== userId))
  }

  const handleAddEmail = () => {
    const email = emailInput.trim()
    if (!email) return

    if (!EMAIL_REGEX.test(email)) {
      setEmailError('Please enter a valid email address')
      return
    }

    const alreadyAdded = inviteEmails.some(item =>
      typeof item === 'string' ? item === email : item.email === email
    )
    if (alreadyAdded) {
      setEmailError('This email has already been added')
      return
    }

    onInviteEmailsChange([
      ...inviteEmails,
      { email, name: nameInput.trim() || null }
    ])
    setEmailInput('')
    setNameInput('')
    setEmailError('')
  }

  const handleRemoveEmail = emailToRemove => {
    onInviteEmailsChange(
      inviteEmails.filter(item =>
        typeof item === 'string'
          ? item !== emailToRemove
          : item.email !== emailToRemove
      )
    )
  }

  /**
   * Searching for someone who has no account is a dead end today — the picker
   * just says "No users found". Hand the user straight over to the invite box,
   * carrying the address they already typed.
   */
  const handleJumpToInvite = () => {
    const term = search.trim()
    if (EMAIL_REGEX.test(term)) {
      setEmailInput(term)
      setEmailError('')
    }
    setOpen(false)
    // Wait for the dropdown to unmount so the box is in its final position.
    requestAnimationFrame(() => {
      emailInputRef.current?.focus()
      emailInputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    })
  }

  const handleEmailKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddEmail()
    }
  }

  const filteredResults = results.filter(
    user => !value.includes(user.id) && !excludeIds.includes(user.id)
  )
  const getUserName = user =>
    [user?.firstName || user?.first_name, user?.lastName || user?.last_name]
      .filter(Boolean)
      .join(' ') ||
    user?.label ||
    user?.email ||
    'User'
  const getUserInitial = user =>
    (user?.firstName || user?.first_name || user?.label || user?.email || '?')
      .charAt(0)

  const triggerLabel = value.length > 0
    ? (maxSelected === 1 ? 'Change selection' : 'Add more people')
    : (maxSelected === 1 ? 'Select a person' : 'Select people')

  return (
    <PickerWrapper ref={wrapperRef}>
      {label && <PickerLabel>{label}</PickerLabel>}

      {/* Selected chips */}
      {(selectedUsers.length > 0 || inviteEmails.length > 0) && (
        <ChipContainer>
          {selectedUsers
            .filter(u => value.includes(u.id))
            .map(user => (
              <Chip key={user.id}>
                <ChipAvatar>
                  {getUserInitial(user)}
                </ChipAvatar>
                <ChipName>
                  {getUserName(user)}
                </ChipName>
                <ChipRemove onClick={() => handleRemove(user.id)}>
                  <CloseIcon style={{ fontSize: 14 }} />
                </ChipRemove>
              </Chip>
            ))}
          {inviteEmails.map(item => {
            const inviteEmail = typeof item === 'string' ? item : item.email
            const inviteName = typeof item === 'string' ? null : item.name
            const chipLabel = inviteName
              ? `${inviteName} (${inviteEmail})`
              : inviteEmail
            return (
              <Chip key={inviteEmail} $invite>
                <ChipAvatar $invite>@</ChipAvatar>
                <ChipName>{chipLabel}</ChipName>
                <ChipRemove onClick={() => handleRemoveEmail(inviteEmail)}>
                  <CloseIcon style={{ fontSize: 14 }} />
                </ChipRemove>
              </Chip>
            )
          })}
        </ChipContainer>
      )}

      {/* Dropdown trigger */}
      <PickerTrigger type='button' $open={open} onClick={handleToggleOpen}>
        <TriggerText $placeholder={value.length === 0}>{triggerLabel}</TriggerText>
        <Chevron $open={open} />
      </PickerTrigger>

      {/* Add yourself directly — works even when you're not in the searchable list */}
      {currentUser?.id && !value.includes(currentUser.id) && !excludeIds.includes(currentUser.id) && (
        <AddMeButton type='button' onClick={() => handleSelect(currentUser)}>
          <PersonAddIcon style={{ fontSize: 15 }} />
          {roleWord(label) ? `Add me as ${roleWord(label)}` : 'Add me'}
        </AddMeButton>
      )}

      {/* Dropdown menu with search on top */}
      {open && (
        <Dropdown>
          <DropdownHeader>
            <SearchWrapper>
              <SearchIconStyled />
              <SearchInput
                type='text'
                placeholder={placeholder}
                value={search}
                onChange={handleSearchChange}
                autoFocus
              />
            </SearchWrapper>
          </DropdownHeader>
          <DropdownList>
            {loading && <DropdownMessage>Loading users...</DropdownMessage>}
            {!loading && filteredResults.length === 0 && (
              <DropdownMessage>
                {search.trim() ? (
                  showEmailInvite ? (
                    <>
                      No users found. You can invite external parties in the{' '}
                      <InviteLink type='button' onClick={handleJumpToInvite}>
                        Invite by email
                      </InviteLink>{' '}
                      box below.
                    </>
                  ) : (
                    'No users found'
                  )
                ) : (
                  'No Building Passport users available'
                )}
              </DropdownMessage>
            )}
            {!loading &&
              filteredResults.map(user => (
                <DropdownItem key={user.id} onClick={() => handleSelect(user)}>
                  <Avatar firstName={user.firstName} lastName={user.lastName} email={user.email} src={user.avatar} size={32} />
                  <UserInfo>
                    <UserName>
                      {user.firstName} {user.lastName}
                    </UserName>
                    <UserEmail>{user.email}</UserEmail>
                  </UserInfo>
                  <TaskCountBadge $count={user.openTaskCount}>
                    {user.openTaskCount} task{user.openTaskCount !== 1 ? 's' : ''}
                  </TaskCountBadge>
                </DropdownItem>
              ))}
          </DropdownList>
        </Dropdown>
      )}

      {/* Email invite */}
      {showEmailInvite && (
        <EmailInviteSection>
          <EmailInviteLabel>
            <PersonAddIcon style={{ fontSize: 16 }} />
            Invite by email
          </EmailInviteLabel>
          <EmailHintText>
            External users can fill in this form without creating an account. Their name and email will be included in the completed form.
          </EmailHintText>
          <EmailInputRow style={{ marginBottom: 8 }}>
            <EmailInput
              type='text'
              placeholder='Name (optional)'
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
            />
          </EmailInputRow>
          <EmailInputRow>
            <EmailInput
              ref={emailInputRef}
              type='email'
              placeholder='user@example.com'
              value={emailInput}
              onChange={e => {
                setEmailInput(e.target.value)
                setEmailError('')
              }}
              onKeyDown={handleEmailKeyDown}
            />
            <AddEmailButton type='button' onClick={handleAddEmail}>
              Add
            </AddEmailButton>
          </EmailInputRow>
          {emailError && <EmailError>{emailError}</EmailError>}
        </EmailInviteSection>
      )}
    </PickerWrapper>
  )
}

export default UserPicker

// --- Styled Components ---

const PickerWrapper = styled.div`
  position: relative;
`

const PickerLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 6px;
`

const ChipContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
`

const Chip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 4px;
  background: ${({ $invite, theme }) =>
    $invite ? theme.colors.linen || '#fbede9' : theme.colors.lightGrey};
  border: 1px solid
    ${({ $invite, theme }) =>
      $invite ? theme.colors.primary + '40' : theme.colors.mediumGrey};
  border-radius: 20px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
`

const ChipAvatar = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${({ $invite, theme }) =>
    $invite ? theme.colors.primary : theme.colors.secondary};
  color: white;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ChipName = styled.span`
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ChipRemove = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  opacity: 0.6;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
`

const SearchWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const SearchIconStyled = styled(SearchIcon)`
  position: absolute;
  left: 12px;
  font-size: 18px !important;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  pointer-events: none;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 14px 10px 36px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};
  background: ${({ theme }) => theme.colors.lightGrey};
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  outline: none;
  transition: all 0.2s;
  box-sizing: border-box;

  &::placeholder {
    color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  }

  &:focus {
    background: white;
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(51, 190, 204, 0.1);
  }
`

const PickerTrigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  font-size: 14px;
  text-align: left;
  background: white;
  border: 1px solid ${({ $open, theme }) => ($open ? theme.colors.secondary : theme.colors.mediumGrey)};
  border-radius: 8px;
  cursor: pointer;
  transition: border-color 0.2s;
  box-sizing: border-box;

  &:hover {
    border-color: ${({ theme }) => theme.colors.secondary};
  }
`

const TriggerText = styled.span`
  color: ${({ $placeholder, theme }) =>
    $placeholder ? theme.colors.grey?.[700] || '#b3b3b3' : theme.colors.haiti};
`

const AddMeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 7px 12px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary};
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    color: white;
  }
`

const Chevron = styled(KeyboardArrowDownIcon)`
  font-size: 20px !important;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.grey?.[700] || '#b3b3b3'};
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 0.2s;
`

const Dropdown = styled.div`
  width: 100%;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  z-index: 100;
  margin-top: 8px;
  box-sizing: border-box;
`

const DropdownHeader = styled.div`
  padding: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrey};
  background: white;
`

const DropdownList = styled.div`
  max-height: 240px;
  overflow-y: auto;
`

const DropdownMessage = styled.div`
  padding: 12px 16px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
`

const InviteLink = styled.button`
  font: inherit;
  display: inline;
  padding: 0;
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.secondary};
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;

  &:hover { filter: brightness(0.92); }
`

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.lightGrey};
  }
`


const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const UserName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
`

const UserEmail = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const TaskCountBadge = styled.div`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 10px;
  background: ${({ $count, theme }) =>
    $count > 10
      ? theme.colors.linen || '#fbede9'
      : $count > 5
        ? '#fffbe6'
        : theme.colors.lightGrey};
  color: ${({ $count, theme }) =>
    $count > 10
      ? theme.colors.error || theme.colors.primary
      : $count > 5
        ? '#ad6800'
        : theme.colors.grey?.[800] || '#999'};
`

const EmailInviteSection = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed ${({ theme }) => theme.colors.mediumGrey};
`

const EmailInviteLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  margin-bottom: 8px;
`

const EmailHintText = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[600] || '#666'};
  margin: 4px 0 8px;
  line-height: 1.4;
`

const EmailInputRow = styled.div`
  display: flex;
  gap: 8px;
`

const EmailInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  background: ${({ theme }) => theme.colors.lightGrey};
  border: 1px solid ${({ theme }) => theme.colors.mediumGrey};
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    background: white;
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(51, 190, 204, 0.1);
  }
`

const AddEmailButton = styled.button`
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.colors.secondary};
    color: white;
  }
`

const EmailError = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.error || theme.colors.primary};
  margin-top: 4px;
`
